import { Tab, Tabs } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import AssignmentSubmissionsTable from './AssignmentSubmissionsTable';
import TestSubmissionsTable from './TestSubmissionsTable';

type LectureOption = { id: number; name: string };
type CourseLike = {
	id: number;
	tabs?: Array<{ sections?: Array<{ courses?: LectureOption[] }> }>;
	unsectioned_lectures?: LectureOption[];
};

type Props = { course: CourseLike };

/**
 * The standalone course's grading queues — the classroom Submissions tab's
 * Tests/Assignments split (admissions don't exist here). Instructor portal
 * only. Sub-tab gating mirrors the classroom rules: the main instructor sees
 * everything, assistants need the classroom sub-tab grants; the loose auth
 * cast is deliberate — this shared source also typechecks in the admin app,
 * whose auth state has no grants. The backend instructorCan gates remain the
 * real enforcement.
 */
const StandaloneSubmissions: React.FC<Props> = ({ course }) => {
	const auth = useSelector((state: any) => state.auth);
	const canGradeTests =
		!auth?.user?.parent_id || !!auth?.grants?.has?.('submissions') || !!auth?.grants?.has?.('course_content');
	const canGradeAssignments =
		!auth?.user?.parent_id || !!auth?.grants?.has?.('submissions') || !!auth?.grants?.has?.('course_content');

	const tabs = useMemo(
		() =>
			[
				canGradeTests ? { key: 'tests', label: 'Tests' } : null,
				canGradeAssignments ? { key: 'assignments', label: 'Assignments' } : null,
			].filter(Boolean) as Array<{ key: string; label: string }>,
		[canGradeTests, canGradeAssignments]
	);
	const [activeTab, setActiveTab] = useState(0);
	const currentKey = tabs[Math.min(activeTab, Math.max(tabs.length - 1, 0))]?.key;

	if (!tabs.length) return null;

	return (
		<Wrapper>
			<Tabs
				value={Math.min(activeTab, tabs.length - 1)}
				onChange={(_, value) => setActiveTab(value)}
				sx={{ marginBottom: '1.5rem' }}
			>
				{tabs.map((tab) => (
					<Tab key={tab.key} label={tab.label} />
				))}
			</Tabs>
			{currentKey === 'tests' && <TestSubmissionsTable course={course} />}
			{currentKey === 'assignments' && (
				<AssignmentSubmissionsTable course={course} />
			)}
		</Wrapper>
	);
};

const Wrapper = styled.div`
	padding: 0 2rem;
`;

export default StandaloneSubmissions;
